import { Injectable } from '@angular/core';
import { ToothDataNew } from '../models/order.models';

export interface XmlParseResult {
  detectedType: string;
  toothData: ToothDataNew[];
  bridges: { start: number; end: number; bridgedTeeth: number[]; bridgeConnectionType: string }[];
  allToothIds: number[];
  matValueName: string;
  refNum: string;
  instructions: string;
  bridgeProps: { [key: string]: string | number };
}

@Injectable({ providedIn: 'root' })
export class DentalXmlService {

  /** Parse a dental XML file into structured order data. Resolves after the FileReader fires. */
  parse(file: File, teethList: number[], anteriorTeeth: number[], types: any[]): Promise<XmlParseResult> {
    return new Promise(resolve => {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        resolve(this.parseXmlContent(e.target.result, teethList, anteriorTeeth, types));
      };
      reader.readAsText(file);
    });
  }

  private parseXmlContent(xmlText: string, teethList: number[], anteriorTeeth: number[], types: any[]): XmlParseResult {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlText, 'text/xml');

    const getPropValue = (name: string): string => {
      const prop = xmlDoc.querySelector(`Property[name="${name}"]`);
      return prop ? prop.getAttribute('value') || '' : '';
    };

    const itemsRawValue = getPropValue('Items');
    // Pre-calculate bridge patterns so we can check teeth against them
    const patterns = itemsRawValue.match(/\d+-\d+/g) || [];
    const detectedType = this.translateDentalType(itemsRawValue, types);

    const newToothData: ToothDataNew[] = [];
    const newBridges: XmlParseResult['bridges'] = [];
    const allToothIds: number[] = [];
    let matValueName = '';
    patterns.forEach(pattern => {
      let [start, end] = pattern.split('-').map(Number);

      start = this.universalToFDI(start);
      end = this.universalToFDI(end);
      const startUniversal = Number(pattern.split('-')[0]);
      const endUniversal = Number(pattern.split('-')[1]);

      const startIndex = teethList.indexOf(startUniversal);
      const endIndex = teethList.indexOf(endUniversal);
      if (startIndex !== -1 && endIndex !== -1) {
        const startIdx = Math.min(startIndex, endIndex);
        const endIdx = Math.max(startIndex, endIndex);
        newBridges.push({
          start: this.universalToFDI(teethList[startIdx]),
          end: this.universalToFDI(teethList[endIdx]),
          bridgedTeeth: teethList
            .slice(startIdx, endIdx + 1)
            .map(t => this.universalToFDI(t)),
          bridgeConnectionType: 'Bridge'
        });
      }
    });
    const toothElements = xmlDoc.querySelectorAll('Object[type="TDM_Item_ToothElement"]');

    toothElements.forEach((element, index) => {
      const tIdValue = element.querySelector('Property[name="ToothNumber"]')?.getAttribute('value');
      const tId = tIdValue
        ? this.universalToFDI(Number(tIdValue))
        : null;
      if (tId && !allToothIds.includes(tId)) {
        allToothIds.push(tId);

        const rawClass = element.querySelector('Property[name="CacheToothTypeClass"]')?.getAttribute('value') || '';
        let finalType = detectedType;
        if (rawClass) {
          const matches = rawClass.match(/[A-Z][a-z]*$/);
          if (matches) finalType = matches[0];
        }

        const rawClassMaterial = element.querySelector('Property[name="toothElementTypeID"]')?.getAttribute('value') || '';
        let extractedMaterial = '';
        if (rawClassMaterial) {
          const match = rawClassMaterial.match(/([A-Z][a-z]+)(?=\d*$)/);
          if (match) extractedMaterial = match[1];
        }

        if (index === 0) matValueName = extractedMaterial;

        const isBridgeTooth = newBridges.some(bridge =>
          bridge.bridgedTeeth.includes(tId)
        );

        const connectionType = isBridgeTooth
          ? 'Strong connection'
          : '';
        newToothData.push(this.createNewToothObject(tId, detectedType, finalType, connectionType, extractedMaterial));
      }
    });



    const bridgeProps: { [key: string]: string | number } = {};

    const counters = {
      anterior: 0,
      posterior: 0,
    };

    newBridges.forEach((bridge) => {
      if (!bridge.bridgeConnectionType) return;

      const group = anteriorTeeth.includes(bridge.start)
        ? "anterior"
        : "posterior";

      const index = counters[group];

      bridgeProps[`bridgeConnectionType-${group}-${index}`] = "Strong connection";
      bridgeProps[`bridgeConnectionType-${group}-frame-${index}`] = "Strong connection";

      counters[group]++;
    });
    return {
      detectedType,
      toothData: newToothData,
      bridges: newBridges,
      allToothIds,
      matValueName,
      refNum: getPropValue('Patient_FirstName') + ' ' + getPropValue('Patient_LastName'),
      instructions: itemsRawValue,
      bridgeProps
    };
  }

  createNewToothObject(tNum: number, type: string, subType: string, connection: string, material: string): ToothDataNew {
    return {
      teeth: tNum,
      selected: false,
      id: Date.now() + Math.random(),
      type,
      subType,
      minimalThickness: 0.5,
      cementGap: 0.015,
      marginGap: 0.5,
      horizontalBorder: 0.015,
      radiusCompensation: 0.5,
      bridgeConnectionType: connection,
      props: {
        Material: material || 'Zirconia',
        Color: 'Any Color'
      }
    };
  }

  private universalToFDI(tooth: number): number {
    const map: Record<number, number> = {
      1: 18, 2: 17, 3: 16, 4: 15, 5: 14, 6: 13, 7: 12, 8: 11,
      9: 21, 10: 22, 11: 23, 12: 24, 13: 25, 14: 26, 15: 27, 16: 28,

      17: 38, 18: 37, 19: 36, 20: 35, 21: 34, 22: 33, 23: 32, 24: 31,

      25: 48, 26: 47, 27: 46, 28: 45, 29: 44, 30: 43, 31: 42, 32: 41
    };

    return map[tooth] ?? tooth;
  }

  buildOrderProps(toothData: any[]): { [key: string]: any } {
    let hasCrown = false;
    let hasPontic = false;
    let hasAnterior = false;
    let hasPosterior = false;

    toothData.forEach(t => {
      if (t.subType === 'Crown') hasCrown = true;
      if (t.subType === 'Pontic') hasPontic = true;
      const tooth = t.teeth;
      if ((tooth >= 11 && tooth <= 23) || (tooth >= 31 && tooth <= 43)) {
        hasAnterior = true;
      } else {
        hasPosterior = true;
      }
    });

    const props: { [key: string]: any } = {
      'Crown wear-front': 'Regular',
      'Crown wear-side': 'Regular',
      'undercuts': 'Yes',
      'ceramic-veneering-anterior': 1.5,
      'ceramic-veneering-posterior': 1.5,
      'ceramic-veneering-bridge': 1.5,
      'h-Occlusion contact': 0.02
    };

    if (hasCrown) {
      props['a-Mesial and distal contact point-true'] = '1/3 of the surface';
      props['a-Occlusal contact-true'] = '0.03 mm light occlusion (standard)';
      props['a-Mesial and distal contact point-false'] = '1/3 of the surface';
      props['a-Occlusal contact-false'] = '0.03 mm light occlusion (standard)';
    }
    if (hasPontic) {
      props['pontic-Pontic design-front'] = 'sattel-a';
      props['pontic-Contact Strength-front'] = 0.03;
      props['pontic-Pontic design-side'] = 'Tangential';
      props['pontic-Contact Strength-side'] = 0.03;
    }
    if (hasAnterior) props['p-Interdental papilla-front'] = 'Partially Open Papilla';
    if (hasPosterior) props['p-Interdental papilla-side'] = 'Partially Open Papilla';

    return props;
  }

  private translateDentalType(xmlString: string, types: any[]): string {
    if (!xmlString) return 'Anatomy';
    const searchStr = xmlString.toLowerCase();
    const dictionary: { [key: string]: string } = {
      'anatomische': 'Anatomy', 'anatomy': 'Anatomy', 'krone': 'Anatomy', 'crown': 'Anatomy',
      'brücke': 'Bridge', 'bridge': 'Bridge',
      'gerüst': 'Frame', 'frame': 'Frame',
      'abutment': 'Abutment'
    };
    const foundKey = Object.keys(dictionary).find(key => searchStr.includes(key));
    if (foundKey) return dictionary[foundKey];
    const directMatch = types.find(t => searchStr.includes(t.name.toLowerCase()));
    return directMatch ? directMatch.name : 'Anatomy';
  }
}      